import ButtonsContainer, { type ButtonsContainerProps } from "@common/controls/Container/Buttons";
import CancelButton from "@common/controls/Form/Buttons/Cancel";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import DynamicForm, { type DynamicFormProps } from "@common/controls/Form/DynamicForm/component";
import { FormControlled, type FormControlledRenderParams, type FormProps, type OnChange } from "@common/controls/Form/component";
import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { useVisibleModal } from "@common/lib/hooks";
import classnames from "classnames";
import { identity, isFunction } from "lodash";
import React, { useRef } from "react";
import Modal from "../../component";
import ConfirmModal, { type ConfirmProviderModal } from "../Confirm/component";
import ConfirmSuccessErrorModal from "../ConfirmSuccessError/component";

export type FormModalConfirmOnClose = boolean | (() => boolean);
export type FormModalBaseProps<T> = Omit<FormProps<T>, "onChange" | "submitContextId"> & {
  cannotClose?: boolean;
  className?: string;
  /** works on FormModalControlled only */
  confirmOnClose?: FormModalConfirmOnClose;
  containerSelector?: string;
  errorMessage?: string | null;
  formClassName?: string;
  height?: string | number;
  isLoading?: boolean;
  isVisible: boolean;
  maxHeight?: string | number;
  maxWidth?: string | number;
  preventCloseOnBackdropClick?: boolean;
  onChange?: OnChange<T>;
  onClose?: () => void;
  onBack?: () => void;
  submit?: (data: T) => void | Promise<any>;
  toast?: string;
  title?: string | null;
  width?: string | number;

  Form: React.ComponentType<any>;
};

export const FormModalBase = <T extends {}>(props: FormModalBaseProps<T>) => {
  const {
    cannotClose,
    className,
    confirmOnClose,
    containerSelector,
    Form,
    formClassName,
    height,
    isLoading,
    isVisible,
    maxHeight,
    maxWidth,
    onBack,
    onClose,
    title,
    width,
    ...formProps
  } = props;
  const formRef = useRef(null);

  const _onClose = React.useCallback(() => {
    if (!onClose) return;

    if (confirmOnClose && formRef.current && "confirm" in formRef.current) {
      (formRef.current["confirm"] as any)(isFunction(confirmOnClose) ? confirmOnClose : undefined, onClose ?? identity);
    } else {
      onClose && onClose();
    }
  }, [confirmOnClose, onClose, formRef]);

  return (
    <Modal
      cannotClose={cannotClose}
      className={classnames("form_modal", className)}
      containerSelector={containerSelector}
      hasBackdrop
      height={height}
      isCentered
      isLoading={isLoading}
      isVisible={isVisible}
      maxHeight={maxHeight || "80vh"}
      maxWidth={maxWidth || "90vw"}
      onBack={onBack}
      onClose={_onClose}
      preventCloseOnBackdropClick={props.preventCloseOnBackdropClick}
      width={width || 450}
    >
      {!!title && <Modal.Header onBackClick={onBack} onCloseClick={onClose} title={title}></Modal.Header>}
      <Modal.Body>
        {!isLoading && (
          <Form
            ref={formRef}
            {...formProps}
            className={formClassName}
            onCancel={onClose}
            onConfirm={onClose}
            isEqual={() => true} // TODO_HACK: prevent modals from triggering confirmation modal
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export interface FormModalProps<T> extends Omit<FormModalBaseProps<T>, "Form"> {}

export type FormModalControlledConfirmProps = {
  title: string;
  message: string;
  caption?: string;
};
export type FormModalSubmit<T> = (data: T) => void | Promise<any>;
export type FormModalControlledRenderParams<T> = FormControlledRenderParams<T>;
export type FormModalControlledPropsBase<T> = Pick<FormModalBaseProps<T>, "isVisible" | "onClose"> & {
  defaultValues: Partial<T>;
  submit: FormModalSubmit<T>;
};
export type FormModalControlledResultModalData = {
  description?: string;
  title?: string;
  text: string;
  icon?: IconType;
  isSuccess: boolean;
};
export type FormModalControlledProps<T> = Omit<FormModalBaseProps<T>, "cannotClose" | "onChange" | "Form"> &
  FormModalControlledPropsBase<T> & {
    confirmOnClose?: FormModalConfirmOnClose;
    LeaveModal?: ConfirmProviderModal;
    onChange?: OnChange<T>;
    hideHeaderWhenSubmitting?: boolean;
    preventCloseWhenSubmitting?: boolean;
    resultModalData?: FormModalControlledResultModalData;
    submitConfirm?: (data: T) => FormModalControlledConfirmProps | undefined;
    toast?: string;

    children: (params: FormControlledRenderParams<T>) => React.ReactElement;
  };
export const FormModalControlled = <T extends object>({
  hideHeaderWhenSubmitting,
  preventCloseWhenSubmitting,
  submit,
  submitConfirm,
  ...props
}: FormModalControlledProps<T>) => {
  const i18n = useI18n();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [confirmData, setConfirmData] = React.useState<(FormModalControlledConfirmProps & { values: T }) | null>();
  const [visibleModal, setVisibleModal, hideModal] = useVisibleModal<"finalModal">();

  const _submit = React.useCallback(
    async (data: T, confirmed?: boolean) => {
      if (submitConfirm && !confirmed) {
        const cma = submitConfirm(data);
        if (cma) {
          setConfirmData({ ...cma, values: data });
          return;
        }
      }

      setIsSubmitting(true);
      try {
        const res = await submit(data);
        setVisibleModal("finalModal");
        return res;
      } finally {
        setIsSubmitting(false);
      }
    },
    [setIsSubmitting, submit, submitConfirm]
  );

  const onSubmitConfirm = React.useCallback(async () => {
    try {
      await _submit(confirmData!.values, true);
    } finally {
      setConfirmData(null);
    }
  }, [_submit, confirmData?.values]);

  return (
    <>
      <FormModalBase
        {...props}
        Form={FormControlled}
        cannotClose={preventCloseWhenSubmitting && isSubmitting}
        title={hideHeaderWhenSubmitting && isSubmitting ? null : props.title}
        toast={submitConfirm ? undefined : props.toast}
        submit={_submit}
      />

      {!!confirmData && (
        <ConfirmModal
          {...confirmData}
          isVisible
          onClose={() => setConfirmData(null)}
          type={{
            label: confirmData.caption ?? i18n.confirm,
            confirmType: "cancel",
            type: "danger"
          }}
          onConfirm={onSubmitConfirm}
        />
      )}

      {props.resultModalData && (
        <ConfirmSuccessErrorModal
          {...props.resultModalData}
          isVisible={visibleModal === "finalModal"}
          onClose={() => {
            props.onClose && props.onClose();
            hideModal();
          }}
        />
      )}
    </>
  );
};

export type DynamicFormModalProps<T extends {}> = DynamicFormProps<T> & {
  className?: string;
  formClassName?: string;
  isVisible: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  width?: string | number;
  height?: string | number;
  maxWidth?: string | number;
  title: string;
  onClose: () => void;
};

export const DynamicFormModal = <T extends {}>(props: DynamicFormModalProps<T>) => (
  <FormModalBase {...props} Form={DynamicForm} />
);

export const FormModalControlledButtons = (props: Omit<ButtonsContainerProps, "children">) => (
  <ButtonsContainer {...props}>
    <CancelButton />
    <SubmitButton />
  </ButtonsContainer>
);
